import React, { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { useEnvironment } from '../../hooks/useEnvironment';
import { ErrorReportingContext } from '../../contexts/ErrorReportingContext';
import { getPrimaryBrowserLanguage } from '../../helpers/languageHelpers';
import { normalizeErrorForReporting } from '../../helpers/errorHelpers';

// Helper to check if Sentry is initialized
const isSentryInitialized = () => {
    return window.__SENTRY_INITIALIZED === true;
};

export const SentryContextProvider = ({ children }) => {
    const { SENTRY, ENVIRONMENT, PORTAL_NAME } = useEnvironment();
    const appInsightsContext = useAppInsightsContext();
    const user = useSelector((state) => state.auth?.user);

    // Patient Portal Specific Data Points
    const patientId = useSelector(
        (state) => state?.chatletEngine?.readOnlyData?.patient?.patientId,
    );
    const campaignName = useSelector((state) => state?.chatletEngine?.campaign?.name);
    const campaignId = useSelector((state) => state?.chatletEngine?.campaign?.campaignId);
    const preferredLanguage = useSelector((state) => state?.chatletEngine?.preferredLanguage);
    const browserLanguage = getPrimaryBrowserLanguage();

    // Set user context when user data changes
    useEffect(() => {
        // Only proceed if Sentry is initialized and enabled
        if (!SENTRY?.ENABLED || !isSentryInitialized() || (!user && !patientId)) return;

        const { userId, userName, person } = user || {};
        const email = person?.email || null;

        Sentry.setUser({
            // Use patientId for patient portal, otherwise use userName
            username: PORTAL_NAME === 'patient' ? `patient: ${patientId}` : userName,
            id: userId,
            email: email,
        });

        // Add extra tags for patient portal
        if (PORTAL_NAME === 'patient') {
            Sentry.setTags({
                portal: PORTAL_NAME,
                patientId: patientId,
                selectedLanguage: preferredLanguage,
                browserLanguage: browserLanguage,
                campaignName: campaignName,
                campaignId: campaignId,
            });
        } else {
            // Add base tags for other portals
            Sentry.setTags({
                username: userName,
                portal: PORTAL_NAME,
            });
        }
    }, [
        user,
        PORTAL_NAME,
        patientId,
        preferredLanguage,
        browserLanguage,
        campaignName,
        campaignId,
        SENTRY?.ENABLED,
    ]);

    const handleError = useCallback(
        (error, errorInfoOriginal = {}) => {
            const { errorCode, ...errorInfo } = errorInfoOriginal;

            // report up the chain to appinsights
            appInsightsContext?.handleException(error, errorInfoOriginal);

            if (!SENTRY?.ENABLED) return;

            // Normalize the error before sending to Sentry to avoid generic messages
            let errorToSend = error;
            try {
                const normalizedError = normalizeErrorForReporting(error);
                if (normalizedError && normalizedError.message !== error.message) {
                    errorToSend = normalizedError;
                }
            } catch (normalizationError) {
                // If normalization fails, continue with original error
                console.warn(
                    'Error normalizing exception for Sentry in ErrorBoundary:',
                    normalizationError,
                );
            }

            // Use withScope approach for all errors (captureReactException is deprecated)
            Sentry.withScope((scope) => {
                scope.setExtras(errorInfo);
                scope.setTags({
                    errorCode,
                    environment: ENVIRONMENT,
                });

                // Add React-specific context if available
                if (errorInfoOriginal.componentStack) {
                    scope.setContext('react', {
                        componentStack: errorInfoOriginal.componentStack,
                    });
                }

                Sentry.captureException(errorToSend);
            });
        },
        [appInsightsContext, SENTRY, ENVIRONMENT],
    );
    
    return (
        <ErrorReportingContext.Provider value={{ handleError }}>
            {children}
        </ErrorReportingContext.Provider>
    );
};

export default SentryContextProvider;
